// #540: shared document helpers, extracted from the post-#529 hardened flow in
// ApplicationDetailPage.openDocument (the canonical copy) so other surfaces
// (FamilyPhotoUploader) stop re-growing the popup-block and blob-revocation
// bugs that flow fixed.

/** Display labels for document upload-slot kinds. Third-copy consolidation
 *  (#540): previously duplicated across ApplicationDetailPage
 *  DOCUMENT_KIND_LABELS, FamilyPhotoUploader KIND_LABEL, and the
 *  ApplicationWizard DOC_SLOTS labels. */
export const DOCUMENT_KIND_LABELS = {
  diagnosis_letter: 'Diagnosis letter',
  income_verification: 'Income verification',
  bill_statement: 'Bill statement',
  child_photo: 'Child photo',
  family_photo: 'Family photo',
} as const satisfies Record<string, string>;

/**
 * How many of one kind an application may carry — the CLIENT's copy of the
 * backend's `DOCUMENT_KIND_SLOT_CAPS` (`backend/src/models/SourceDocument.js`).
 *
 * The two are pinned equal, key for key, by
 * `backend/tests/contract/document-slot-cap-parity.test.js`. That pin is the
 * whole reason this constant exists rather than each surface deciding for
 * itself, and #1425 is why it was needed: the wizard's `DOC_SLOTS` hand-wrote
 * `multiple`/`maxCount` per slot while the server hand-wrote a cap map, and the
 * two disagreed about `income_verification` — a single slot in the UI, uncapped
 * on the server. A family who confirmed "replace this paystub" got a dialog
 * promising the old one would no longer be attached, a 201, and two paystubs
 * back on the next page load, because the server's replace path is honored
 * only where the slot is single and the server did not think it was.
 *
 * Cap 1 IS single-slot: one document, replaced rather than added to.
 *
 * #2472: the child's photo and the family's are two slots, up to 3 each.
 *
 * 2026-11-03 hotfix: income verification holds up to 10 — two months of pay
 * stubs for each working adult. Cap 1 had made a second stub replace the first.
 */
export const DOCUMENT_KIND_SLOT_CAPS = {
  diagnosis_letter: 1,
  income_verification: 10,
  bill_statement: 5,
  child_photo: 3,
  family_photo: 3,
} as const satisfies Record<keyof typeof DOCUMENT_KIND_LABELS, number>;

/**
 * #2472 legacy rule, the client copy of the server's
 * `backend/src/helpers/childPhotoSlot.js`. Before the child's photo had its
 * own kind, #2462 asked for both photos in the one `family_photo` slot and
 * counted two uploads, so an application holding two or more family photos
 * also fills the child-photo slot. A single family photo never does.
 */
export const LEGACY_FAMILY_PHOTOS_FOR_CHILD_SLOT = 2;

/** Staff words for a child-photo slot filled by the legacy rule, read after
 *  the kind ("Child photo · covered by family photos"). Staff UI is English-only. */
export const CHILD_PHOTO_COVERED_WORDS = 'covered by family photos';

/** The longer sentence for a tooltip. */
export const CHILD_PHOTO_COVERED_TITLE =
  'No child photo on file, but the family sent two family photos before the child photo had its own slot. '
  + 'Those count for the child photo, so nothing is missing.';

export interface PresentDocumentKinds {
  /** Kinds with at least one document, plus `child_photo` when the legacy rule fills it. */
  kinds: ReadonlySet<string>;
  /** True only when the child slot is filled by the family photos alone. */
  childPhotoCoveredByFamilyPhotos: boolean;
}

/**
 * Which kinds an application holds — the ONE client predicate behind the
 * family wizard's submit block (`components/apply/requiredDocuments.ts`), the
 * review page's "N of 5 received" (`requiredDocumentCount`) and the staff
 * files strip, so none of them disagrees with the server's submit check.
 */
export function presentDocumentKinds(documents: readonly { kind: string }[]): PresentDocumentKinds {
  const kinds = new Set(documents.map((d) => d.kind));
  const familyPhotos = documents.filter((d) => d.kind === 'family_photo').length;
  const childPhotoCoveredByFamilyPhotos = !kinds.has('child_photo')
    && familyPhotos >= LEGACY_FAMILY_PHOTOS_FOR_CHILD_SLOT;
  if (childPhotoCoveredByFamilyPhotos) kinds.add('child_photo');
  return { kinds, childPhotoCoveredByFamilyPhotos };
}

/**
 * Lane L12: the review page's document count. Counts the REQUIRED kinds that
 * are present against the number of required kinds, by the same rule as the
 * server's `missingRequiredDocuments()`: one active document fills a kind
 * (#2472 split the photos into two kinds, so no kind needs two), and two
 * family photos also fill the child slot (`presentDocumentKinds`). Every other
 * file is an extra: a second letter, a third photo, a kind TRF does not
 * require. Before this the page counted files against kinds, so two photos
 * read "5 of 4 received".
 */
export interface RequiredDocumentCount {
  /** Required kinds that hold a document. */
  received: number;
  /** Required kinds. */
  required: number;
  /** Files beyond the ones that fill the required kinds. */
  extra: number;
}

export function requiredDocumentCount(
  documents: readonly { kind: string }[],
  required: readonly string[],
): RequiredDocumentCount {
  // #2472: the legacy rule's second family photo fills the child slot, so it
  // is a used file, not an extra.
  const present = presentDocumentKinds(documents).kinds;
  const kinds = [...new Set(required)];
  const received = kinds.filter((kind) => present.has(kind)).length;
  return { received, required: kinds.length, extra: Math.max(0, documents.length - received) };
}

/** The highlights strip's Documents cell: "4 of 4 received", or "4 of 4 required · 2 more". */
export function requiredDocumentsStripWords({ received, required, extra }: RequiredDocumentCount): string {
  if (required === 0) return `${extra} received`;
  return extra > 0 ? `${received} of ${required} required · ${extra} more` : `${received} of ${required} received`;
}

/** The documents block's header line, the same count in a sentence. */
export function requiredDocumentsSummaryWords({ received, required, extra }: RequiredDocumentCount): string {
  if (required === 0) return `${extra} document${extra === 1 ? '' : 's'} received · none required`;
  const base = `${received} of ${required} required documents received`;
  return extra > 0 ? `${base} · ${extra} more` : base;
}

/**
 * The statuses in which TRF is still collecting an application's documents.
 *
 * The queue's "Diagnosis letter: not received" chips are a worklist — things
 * to chase — and there is nothing to chase on an application that has been
 * decided: an admitted family's documents live on its Family record, and a
 * declined or withdrawn one is closed. Showing the chips there read as an
 * accusation ("admitted without a diagnosis letter") rather than a task
 * (Victor, 2026-09-15). The server's chase FILTER keeps the same set
 * (`backend/src/services/documentsSummary.js` CHASE_OPEN_STATUSES).
 */
export const DOCUMENT_CHASE_STATUSES: ReadonlySet<string> = new Set([
  'draft', 'submitted', 'under_review', 'needs_more_info', 'staff_recommended', 'waitlisted',
]);

/** Is this kind a single slot — one document, replaced rather than added to?
 *  The one predicate behind every replace affordance (the family wizard's F-2
 *  tile, the staff attach control), mirroring the backend helper of the same
 *  name. Unknown kinds are not single-slot: nothing may be silently retired on
 *  the strength of a kind this map has never heard of. */
export function isSingleSlotKind(kind: string): boolean {
  return (DOCUMENT_KIND_SLOT_CAPS as Record<string, number>)[kind] === 1;
}

/**
 * The document kinds a staff surface EXPECTS on an application (#1007 / AP-24).
 *
 * "Expected", never "required" — D-SE-11. Nothing is a required document today:
 * the wizard tells families *"all optional — skip what you don't have"*, `DocSlot`
 * carries no `required`, and submit validates none. Whether TRF should require
 * any of these is #1115, TRF's to answer and still open. A staff surface that
 * said "required" would state a rule the platform does not keep with the very
 * family it is about to judge.
 *
 * Mirrors the backend's `APPLICATION_KINDS` (SourceDocument.js) — the same
 * five (#2472 added `child_photo`), in the order staff read them. A named
 * constant so the answer to #1115 is one edit and a test update, never a rebuild.
 */
export const EXPECTED_APPLICATION_DOC_KINDS = [
  'diagnosis_letter',
  'income_verification',
  'bill_statement',
  'child_photo',
  'family_photo',
] as const satisfies readonly (keyof typeof DOCUMENT_KIND_LABELS)[];

/**
 * Labels for kinds that appear on an application but are NOT upload slots.
 *
 * A SECOND map rather than four more keys in `DOCUMENT_KIND_LABELS`, and the
 * reason is the type above: `DOCUMENT_KIND_SLOT_CAPS` is declared as
 * `Record<keyof typeof DOCUMENT_KIND_LABELS, number>`, so every label key must
 * carry a slot cap. That coupling is deliberate and worth keeping — it is what
 * pins the client's caps to the server's — but it means adding a
 * non-uploadable kind to the label map would force a fabricated cap for a slot
 * that does not exist, and `EXPECTED_APPLICATION_DOC_KINDS` would start
 * offering staff a chase row for a document no one can send.
 *
 * The split mirrors the backend's exactly: `APPLICATION_KINDS` is what may be
 * uploaded, `APPLICATION_DOC_KINDS` is what may be read
 * (`backend/src/models/SourceDocument.js`). This map covers the difference.
 *
 * `executed_offer` (#1073 / AP-52) is the signed agreement TRF pulls back from
 * DocuSign when the envelope completes. No door accepts one, so it has no
 * slot, no cap and no tile — only a name on the profile's document list.
 */
export const SYSTEM_DOC_KIND_LABELS = {
  executed_offer: 'Signed admission letter',
} as const satisfies Record<string, string>;

/**
 * Label for an arbitrary kind slug. Reads the upload-slot labels first, then
 * the system-filed ones.
 *
 * An unknown kind HUMANIZES rather than returning the raw slug. D-SE-18's
 * guarantee is that a family is never shown a storage key, not that they are
 * never shown one we remembered to list -- and this function feeds the
 * family's own status panel, so `?? kind` put `bill_statement` in front of a
 * parent the moment a kind was added to SourceDocument and forgotten here.
 * Mirrors `backend/src/helpers/documentFlags.js`; pinned by
 * `backend/tests/contract/document-kind-label-parity.test.js`.
 */
export function documentKindLabel(kind: string): string {
  return (DOCUMENT_KIND_LABELS as Record<string, string>)[kind]
    ?? (SYSTEM_DOC_KIND_LABELS as Record<string, string>)[kind]
    ?? String(kind ?? '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** #558: fallback-filename extension for a served document mime. Mirrors the
 *  backend's SAFE_MIME allowlist (helpers/streamDocumentFile.js) — anything
 *  else is served as octet-stream, where no extension is honest. Used by the
 *  shared download path when the caller's downloadAs name (typically the
 *  'bill-statement' fallback for a missing stored filename) has no extension. */
export function extensionForMime(mime: string | undefined): string {
  switch (mime) {
    case 'application/pdf':
      return '.pdf';
    case 'image/jpeg':
    case 'image/jpg':
      return '.jpg';
    case 'image/png':
      return '.png';
    default:
      return '';
  }
}

/** Minimal structural type for the useToast() `toast` function — declared
 *  here so this lib doesn't import component code. */
export type DocumentToastFn = (opts: {
  variant?: 'destructive';
  title?: string;
  description?: string;
}) => void;

/**
 * Module-level blob-URL lifetime policy (#556 review rework). Every URL
 * created by openAuthedDocument is tracked here and revoked by ONE uniform
 * policy, with no per-component wiring:
 *
 * - a per-URL 10-minute TTL — generous headroom for the opened tab to finish
 *   loading the blob (the tab holds its own reference once loaded), without
 *   accumulating blobs for a whole long-lived session;
 * - a revoke-everything sweep on window 'pagehide' (navigation / tab close).
 *
 * Deliberately NOT tied to any component's unmount: the first registry design
 * revoked on owner unmount, but FamilyPhotoUploader mounts inside
 * FamilyIntakeDrawer's Dialog/TabsContent, so closing the drawer or switching
 * tabs unmounted it and revoked URLs a just-opened browser tab was still
 * reading — worse than the old 60s timer it replaced. A module-level TTL has
 * no such coupling and no boilerplate for adopters to copy.
 */
export const BLOB_URL_TTL_MS = 10 * 60 * 1000;

const pendingBlobUrls = new Map<string, ReturnType<typeof setTimeout>>();

/** Track a blob object URL for TTL + pagehide revocation. Re-tracking an
 *  already-tracked URL refreshes its TTL (createObjectURL never repeats in
 *  production, but mocked URLs in tests do). */
export function trackBlobUrl(url: string): void {
  const existing = pendingBlobUrls.get(url);
  if (existing) clearTimeout(existing);
  pendingBlobUrls.set(
    url,
    setTimeout(() => {
      pendingBlobUrls.delete(url);
      URL.revokeObjectURL(url);
    }, BLOB_URL_TTL_MS),
  );
}

/** Revoke every tracked URL now (pagehide, or a test's cleanup). */
export function revokeAllBlobUrls(): void {
  for (const [url, timer] of pendingBlobUrls) {
    clearTimeout(timer);
    URL.revokeObjectURL(url);
  }
  pendingBlobUrls.clear();
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', revokeAllBlobUrls);
}

export interface OpenAuthedDocumentOptions {
  /** useToast()'s toast — failures must surface, never silently swallow. */
  toast: DocumentToastFn;
  /** When set, save the blob via a hidden anchor (browser download) with this
   *  filename instead of opening a tab — bill_statement is download-only
   *  (D-W2-20): the server dispositions it as an attachment, and opening a
   *  blob URL in a tab would sidestep that. */
  downloadAs?: string;
}

/**
 * Open (or download) an auth-gated document. The route needs the auth header,
 * so a plain <a href> can't carry the token: fetch the blob via `fetchBlob`,
 * then navigate a pre-opened tab to the blob URL.
 *
 * MUST be called synchronously from the user-gesture handler (no await before
 * it): the tab is opened SYNCHRONOUSLY before any await, because popup
 * blockers (Safari always) silently return null for window.open calls that
 * happen after an await. No 'noopener' feature string — per spec that makes
 * window.open return null, and we need the handle to navigate it once the
 * blob arrives; nulling .opener instead gives the same isolation.
 *
 * Failure paths: popup blocked → destructive toast, no fetch; fetch failed →
 * close the pre-opened tab + destructive toast.
 */
export async function openAuthedDocument(
  fetchBlob: () => Promise<Blob>,
  { toast, downloadAs }: OpenAuthedDocumentOptions,
): Promise<void> {
  let win: Window | null = null;
  if (!downloadAs) {
    win = window.open('', '_blank');
    if (!win) {
      toast({
        variant: 'destructive',
        title: "Couldn’t open document",
        description: 'Your browser blocked the popup. Allow popups for this site and try again.',
      });
      return;
    }
    win.opener = null;
  }
  try {
    const blob = await fetchBlob();
    const url = URL.createObjectURL(blob);
    trackBlobUrl(url);
    if (downloadAs) {
      // #558: an extension-less name (the pages' fallback when the stored
      // filename is empty) gets its extension from the served blob's mime,
      // so the saved file opens in the right app.
      const hasExtension = /\.[A-Za-z0-9]+$/.test(downloadAs);
      const a = document.createElement('a');
      a.href = url;
      a.download = hasExtension ? downloadAs : `${downloadAs}${extensionForMime(blob.type)}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      win!.location.href = url;
    }
  } catch {
    win?.close();
    toast({
      variant: 'destructive',
      title: "Couldn’t open document",
      description: 'The file didn’t load. Please try again.',
    });
  }
}
